/**
 * WorkforceRegionCompare — Priya wave, screen 2 of 5. Port of
 * regional-screens.jsx's PeerComparison: every branch on the same scale.
 * Op: getRegionCompare (REQ-039, kit-served; regionId clamped to the caller).
 */
import { T, alpha } from "@v0y4ge/jrni-ui";
import { Screen, PageHeader } from "../../shell/chrome";
import { DataSourceStrip } from "../../shell/opSource";
import { useWfResource } from "./useWorkforce";
import { getRegionCompare, sessionScope, NO_REGION_SCOPE_MSG, type RegionCompareRow } from "./wf-api";
import { useT, t as i18nT } from "../../lib/i18n";

/** Metric vocabulary — id order matches the contract's `metrics` list. */ // ui-config
const METRIC_META: { id: keyof RegionCompareRow["values"]; label: string; unit: string; lowerIsBetter?: boolean }[] = [
  { id: "conversion", label: "Conversion", unit: "%" },
  { id: "nps", label: "NPS", unit: "" },
  { id: "wait", label: "Avg wait", unit: "m", lowerIsBetter: true },
  { id: "adherence", label: "Adherence", unit: "%" },
  { id: "revPerFte", label: "Rev / FTE", unit: "k" },
];
const TREND_META = { up: { glyph: "↑", color: T.green1 }, flat: { glyph: "→", color: T.soft }, down: { glyph: "↓", color: T.red1 } } as const;

export default function WorkforceRegionCompare() {
  const t = useT();
  const { regionId } = sessionScope(); // §3.2 — session scope only, no tenant-named default
  const { data, loading, error } = useWfResource(`region-compare:${regionId ?? "none"}`, () => regionId ? getRegionCompare(regionId) : Promise.reject(new Error(NO_REGION_SCOPE_MSG)));
  const rows = data?.rows ?? [];
  const best: Partial<Record<string, number>> = {};
  const worst: Partial<Record<string, number>> = {};
  for (const m of METRIC_META) {
    const vals = rows.map((r) => r.values[m.id]);
    if (vals.length === 0) continue;
    best[m.id] = m.lowerIsBetter ? Math.min(...vals) : Math.max(...vals);
    worst[m.id] = m.lowerIsBetter ? Math.max(...vals) : Math.min(...vals);
  }
  return (
    <Screen rail="wf-region-compare">
      <PageHeader title={t("workforce.regionCompare.peerComparison", "Peer comparison")} subtitle={t("workforce.regionCompare.branchByBranchOnTheSameScaleConversionNpsWaitTimeAdherence", "Branch by branch on the same scale — conversion, NPS, wait time, adherence, revenue per FTE.")} breadcrumb={null} />
      <DataSourceStrip screen="wfregioncompare" ops={["getRegionCompare"]} reqId="REQ-039" />
      <div style={{ flex: 1, overflow: "auto", padding: "14px 24px 32px", display: "flex", flexDirection: "column", gap: 14 }}>
        {error ? <div style={{ padding: 40, textAlign: "center", color: T.soft }}>{t("workforce.regionCompare.couldnTLoadTheComparison", "Couldn't load the comparison:")}{" "}{error}</div>
          : loading || !data ? <div style={{ padding: 40, textAlign: "center", color: T.soft, fontSize: 14 }}>{t("common.status.loading", "Loading…")}</div>
          : <>
            <div style={{ border: `1px solid ${T.border}`, borderRadius: 12, background: T.white, overflow: "hidden" }}>
              <div style={{ display: "grid", gridTemplateColumns: `56px 1.7fr repeat(${METRIC_META.length}, 1fr) 70px`, gap: 10, padding: "9px 18px", background: T.n6, fontSize: 10.5, fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.05, color: T.soft }}>
                <span>{t("workforce.regionCompare.rank", "Rank")}</span><span>{t("workforce.regionCompare.branch", "Branch")}</span>
                {METRIC_META.map((m) => <span key={m.id} style={{ textAlign: "right" }}>{m.label}</span>)}
                <span style={{ textAlign: "right" }}>{t("workforce.regionCompare.trend", "Trend")}</span>
              </div>
              {rows.map((r) => (
                <div key={r.branchId} data-testid="wfregion-compare-row" data-id={r.branchId} style={{ display: "grid", gridTemplateColumns: `56px 1.7fr repeat(${METRIC_META.length}, 1fr) 70px`, gap: 10, padding: "12px 18px", borderBottom: `1px solid ${T.n6}`, alignItems: "center", fontSize: 12.5, background: r.rank === 1 ? alpha(T.green1, 5) : "transparent" }}>
                  <span style={{ fontFamily: T.fontMono, fontWeight: 700, color: r.rank <= 2 ? T.green1 : r.rank >= rows.length - 1 ? T.orange1 : T.n2 }}>#{r.rank}</span>
                  <b style={{ color: T.n1 }}>{r.name}</b>
                  {METRIC_META.map((m) => {
                    const v = r.values[m.id];
                    const isBest = v === best[m.id]; const isWorst = v === worst[m.id] && best[m.id] !== worst[m.id];
                    const color = isBest ? T.green1 : isWorst ? T.red1 : T.n1;
                    return (
                      <span key={m.id} style={{ textAlign: "right" }}>
                        <span style={{ fontFamily: T.fontMono, fontWeight: isBest || isWorst ? 700 : 500, color, background: isBest || isWorst ? alpha(color, 10) : "transparent", padding: "2px 7px", borderRadius: 6 }}>
                          {m.id === "revPerFte" ? /* TODO(i18n): replace template-literal with t('workforce.regionCompare.text', { ... }) */`$${v}${m.unit}` : /* TODO(i18n): replace template-literal with t('workforce.regionCompare.text2', { ... }) */`${v}${m.unit}`}
                        </span>
                      </span>
                    );
                  })}
                  <span style={{ textAlign: "right", fontSize: 15, fontWeight: 700, color: TREND_META[r.trend].color }} title={i18nT("workforce.regionCompare.trendRTrend.icu", { value0: r.trend })}>{TREND_META[r.trend].glyph}</span>
                </div>
              ))}
            </div>
            <span style={{ fontSize: 11.5, color: T.soft }}>{t("workforce.regionCompare.rankBlendsAllFiveMetricsGreenBestInRegionForThatMetric", "Rank blends all five metrics. Green = best in region for that metric, red = the laggard; the trend arrow follows coverage over the last four weeks.")}</span>
          </>}
      </div>
    </Screen>
  );
}
